import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "./consent-forms.css";

const PhysicianConsentForm = ({ onClose, onSubmit, isEditMode = false, existingData = null }) => {
  const [formData, setFormData] = useState({
    physicianName: "",
    physicianLicense: "",
    labRepName: "",
    labRepTitle: "",
    authorizeTest: false,
    acknowledgeTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with existing data if in edit mode
  useEffect(() => {
    if (isEditMode && existingData) {
      setFormData({
        physicianName: existingData.physician_name || "",
        physicianLicense: existingData.physician_license || "",
        labRepName: existingData.lab_rep_name || "",
        labRepTitle: existingData.lab_rep_title || "",
        authorizeTest: existingData.consent_checkboxes?.authorizeTest || false,
        acknowledgeTerms: existingData.consent_checkboxes?.acknowledgeTerms || false,
      });
    }
  }, [isEditMode, existingData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.physicianName.trim()) {
      newErrors.physicianName = "Physician name is required";
    }

    if (!formData.physicianLicense.trim()) {
      newErrors.physicianLicense = "Physician license number is required";
    }

    // Laboratory representative fields are optional

    if (!formData.authorizeTest) {
      newErrors.authorizeTest =
        "You must authorize the laboratory to perform the test";
    }

    if (!formData.acknowledgeTerms) {
      newErrors.acknowledgeTerms =
        "You must acknowledge the laboratory's sample handling and withdrawal terms";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        const now = new Date();
        const signatureDate = now.toISOString().split("T")[0];
        const signatureTime = now.toTimeString().split(" ")[0];

        const consentData = {
          consent_type: "provider",
          form_version: "v1.0",
          signature_date: signatureDate,
          signature_time: signatureTime,
          requisition_form_version: "v1.0",
          consent_checkboxes: {
            authorizeTest: formData.authorizeTest,
            acknowledgeTerms: formData.acknowledgeTerms,
          },
          physician_name: formData.physicianName,
          physician_license: formData.physicianLicense,
          lab_rep_name: formData.labRepName,
          lab_rep_title: formData.labRepTitle,
          // Include existing consent form ID for updates
          ...(isEditMode && existingData?.id && { id: existingData.id }),
        };

        await onSubmit(consentData);
      } catch (error) {
        console.error("Error submitting consent form:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const isSubmitDisabled =
    !formData.authorizeTest ||
    !formData.acknowledgeTerms ||
    !formData.physicianName.trim() ||
    !formData.physicianLicense.trim() ||
    isSubmitting;

  return (
    <motion.div
      className="consent-form-container"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
    >
      <div className="consent-form-header">
        <h1>Physician Consent Form for Use of Genetic Testing Services</h1>
        <p>
          Please read each section carefully. Contact our Support at{" "}
          <a
            href="https://mail.google.com/mail/?view=cm&fs=1&to=provider@syndicate.bio"
            target="_blank"
            rel="noopener noreferrer"
          >
            provider@syndicate.bio
          </a> with any questions.
        </p>
      </div>

      <div className="consent-form-content">
        <div className="consent-section">
          <h2>1. Physician Request and Authorization</h2>
          <p>
            I, the undersigned physician or authorized healthcare provider, am requesting genetic testing
            services on behalf of my patient(s).
          </p>
          <p>
            I confirm that I am licensed to practice medicine and that I will order, interpret, and act upon all
            test results within the scope of my professional practice.
          </p>
        </div>

        <div className="consent-section">
          <h2>2. Intended Use and Scope</h2>
          <p>I understand that the genetic tests provided by this laboratory are intended to:</p>
          <ul>
            <li>Identify inherited variants associated with disease risk or drug response</li>
            <li>Inform patient management, screening, and treatment decisions</li>
            <li>Potentially guide personalized therapy or preventive measures</li>
          </ul>
          <p>
            I agree to use test results only for clinical decision-making relevant to my patient's care.
          </p>
        </div>

        <div className="consent-section">
          <h2>3. Patient Informed Consent Responsibility</h2>
          <p>
            I acknowledge that obtaining valid informed consent from each patient is my responsibility.
            Specifically, I will:
          </p>
          <ul>
            <li>Explain the purpose, benefits, and limitations of genetic testing</li>
            <li>Discuss potential physical, psychological, and familial implications</li>
            <li>Provide or refer the patient to pre-test genetic counseling, as needed</li>
            <li>Document the patient's consent in their medical record</li>
          </ul>
        </div>

        <div className="consent-section">
          <h2>4. Data Use, Privacy, and Confidentiality</h2>
          <p>I understand how patient samples and data will be handled:</p>
          <ul>
            <li>The laboratory will process, store, and report results in compliance with applicable privacy laws.</li>
            <li>De-identified data may be used for assay validation, quality control, and research.</li>
            <li>Any data sharing with third-party collaborators will exclude personally identifiable information.</li>
          </ul>
          <p>
            I will ensure that my practice's data-sharing policies align with the laboratory's privacy protocols.
          </p>
        </div>

        <div className="consent-section">
          <h2>5. Professional Obligations and Follow-Up</h2>
          <p>I agree to:</p>
          <ul>
            <li>Review and interpret reports promptly upon receipt</li>
            <li>Communicate results and their clinical significance to patients in a timely manner</li>
            <li>Provide or facilitate post-test genetic counseling for patients and their families</li>
            <li>Incorporate testing outcomes into the patient's ongoing care plan</li>
          </ul>
        </div>

        <div className="consent-section">
          <h2>6. Genetic Counseling Collaboration</h2>
          <p>Genetic counseling enhances patient understanding of test implications. I will:</p>
          <ul>
            <li>Offer or refer patients to certified genetic counselors before and after testing</li>
            <li>Coordinate with the laboratory's genetic counseling service if needed</li>
            <li>Ensure counseling notes are included in the patient's medical record</li>
          </ul>
        </div>

        <div className="consent-section">
          <h2>7. Liability and Indemnification</h2>
          <p>I acknowledge that:</p>
          <ul>
            <li>The laboratory's analytical performance is validated, but no test is 100% comprehensive or error-free.</li>
            <li>Clinical interpretation remains my professional responsibility.</li>
            <li>I will indemnify and hold harmless the laboratory for decisions made based on test results.</li>
          </ul>
        </div>

        <div className="consent-section">
          <h2>8. Test Withdrawal and Sample Handling</h2>
          <p>I understand that:</p>
          <ul>
            <li>I may request cancellation of testing and sample destruction prior to analysis.</li>
            <li>Once testing begins, the laboratory may retain residual samples for quality assurance or compliance.</li>
            <li>Completed reports are final and samples cannot be returned.</li>
          </ul>
        </div>

        <div className="consent-section">
          <h2>9. Future Research Use of De-identified Data</h2>
          <p>
            I grant permission for the laboratory to use de-identified patient data in future research studies
            and publications aimed at improving genetic testing methods and expanding scientific knowledge.
          </p>
        </div>

        <div className="consent-section declaration">
          <h2>Physician Declaration</h2>
          <p>
            By ticking this box, I certify that I have read, understand, and agree to the terms outlined above,
            and that I will uphold all responsibilities in ordering and interpreting genetic tests for my patients.
          </p>

          <div className="checkbox-group">
            <label className="custom-checkbox">
              <input
                type="checkbox"
                name="authorizeTest"
                checked={formData.authorizeTest}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                I authorize the laboratory to perform the requested test(s) on my sample. (required)
              </span>
            </label>

            <label className="custom-checkbox">
              <input
                type="checkbox"
                name="acknowledgeTerms"
                checked={formData.acknowledgeTerms}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                I acknowledge the laboratory's sample handling and withdrawal terms.
              </span>
            </label>
          </div>

          {errors.authorizeTest && <span className="error">{errors.authorizeTest}</span>}
          {errors.acknowledgeTerms && <span className="error">{errors.acknowledgeTerms}</span>}
        </div>

        <div className="consent-section input-form-field">
          <h2>Physician information</h2>
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                name="physicianName"
                value={formData.physicianName}
                onChange={handleChange}
                placeholder="Enter name here"
                className={errors.physicianName ? "error" : ""}
              />
              {errors.physicianName && <span className="error">{errors.physicianName}</span>}
            </div>
            <div className="form-group">
              <label>License number</label>
              <input
                type="text"
                name="physicianLicense"
                value={formData.physicianLicense}
                onChange={handleChange}
                placeholder="Enter license number"
                className={errors.physicianLicense ? "error" : ""}
              />
              {errors.physicianLicense && <span className="error">{errors.physicianLicense}</span>}
            </div>
          </div>
        </div>

        <div className="consent-section input-form-field">
          <h2>Laboratory Representative Information(optional)</h2>
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                name="labRepName"
                value={formData.labRepName}
                onChange={handleChange}
                placeholder="Enter name here"
                className={errors.labRepName ? "error" : ""}
              />
              {errors.labRepName && <span className="error">{errors.labRepName}</span>}
            </div>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                name="labRepTitle"
                value={formData.labRepTitle}
                onChange={handleChange}
                placeholder="Enter title here"
                className={errors.labRepTitle ? "error" : ""}
              />
              {errors.labRepTitle && <span className="error">{errors.labRepTitle}</span>}
            </div>
          </div>
        </div>

        <div className="consent-section">
          <p className="thank-you-message">
            Thank you for partnering with us to deliver responsible and effective genetic testing services.
          </p>
        </div>
      </div>

      <div className="consent-form-footer">
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          className={`submit-button ${isSubmitDisabled ? "disabled" : ""}`}
        >
          {isSubmitting 
            ? (isEditMode ? "Updating..." : "Submitting...") 
            : (isEditMode ? "Update form" : "Submit form")
          }
        </button>
      </div>
    </motion.div>
  );
};

export default PhysicianConsentForm;
